import useCommon from '@utils/hooks/common';
import {Button, Table, DrawerProps, Skeleton, ConfigProvider} from 'antd';
import React, {useEffect, useState, useRef} from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import dayjs from 'dayjs';
import BigNumber from 'bignumber.js';
import useRequest from '@utils/hooks/useRequestCustom';
import WinnerCard from '@components/WinnerCard';
import PlayerInfo from '@components/PlayerInfo';
import MultiUnitStatistic from '@components/game-room/MultiUnitStatistic';
import gameApi from '@utils/api/game.api';
import {compactFormatter} from '@utils/index';
import {UNIT_TYPE} from '@constant/game';
import {PokerCard} from '@interface';
import styles from './styles';
import {LoadingOutlined} from '@ant-design/icons';

interface HistoryDrawerProps extends DrawerProps {
  closeDrawer?: () => void;
  roomNumber?: number | string;
  isVisbileChange?: boolean;
}

interface GamePlayersInterface {
  playerId: number;
  displayName: string;
  icon?: string;
}

interface PlayersHandListInterface {
  playerId: number;
  playerHand: Array<PokerCard>;
}

const API_PARAMS = {
  ROOM_AMOUNT: 8,
  PAGE_NUMBER: 1,
};

const HistoryPanel = (props: HistoryDrawerProps) => {
  const {closeDrawer, roomNumber, visible, isVisbileChange} = props;
  const {classes, socket, game, t, user, theme} = useCommon({
    withStyles: styles,
    observers: ['socket', 'user', 'game'],
    withTranslation: ['history_drawer'],
  });

  const [isRecentGamesTableShown, setIsRecentGamesTableShown] = useState<boolean>(true);
  const [hasMoreData, setHasMoreData] = useState<boolean>(false);
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
  const [recentGameList, setRecentGameList] = useState([]);
  const pageNumberRef = useRef<number>(1);

  const {
    loading: getLoading,
    runAsync: getHistoryDetail,
    data: historyDetailData,
  } = useRequest(gameApi.getHistoryDetail, {
    manual: true,
  });

  const playerToken = user?.getPlayerToken();

  const getData = async () => {
    try {
      setHasMoreData(true);
      setIsTableLoading(true);

      const historyList = await gameApi.getRecentGames({
        roomCode: roomNumber,
        playerToken,
        limit: API_PARAMS.ROOM_AMOUNT,
        page: pageNumberRef.current,
      });

      setRecentGameList(historyList);
      if (historyList.length < API_PARAMS.ROOM_AMOUNT) {
        setHasMoreData(false);
      }
      setIsTableLoading(false);
    } catch (error) {
      setIsTableLoading(false); // Set loading to false in case of an error
    }
  };

  useEffect(() => {
    setIsRecentGamesTableShown(true);
    setRecentGameList([]);
    pageNumberRef.current = 1;
    getData();
  }, [isVisbileChange]);

  const tableLocale = {
    emptyText: <div style={{color: theme.textColor}}>{isTableLoading ? null : t('no_data')}</div>,
  };

  const spinIcon = <LoadingOutlined className={classes.spinIcon} spin />;

  const tableLoading = {
    spinning: isTableLoading,
    indicator: spinIcon,
  };

  const turnName = {
    1: t('small_blind'),
    2: t('pre_flop'),
    3: t('flop'),
    4: t('turn'),
    5: t('river'),
  };

  const getWinnerIdArray = data => {
    if (data.result) {
      return data.result[0].potWinnerIds;
    }
    return [];
  };

  const recentGamesColumn = [
    {
      title: t('time'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: value => {
        return dayjs(value).format('YYYY/MM/DD HH:mm:ss');
      },
    },
    {
      title: t('winner'),
      key: 'winners',
      render: (data, id) => (
        <div className={classes.tableCellWithTwoRow} key={id}>
          <div className={classes.dateSpan}>
            {data.players
              .filter(g => getWinnerIdArray(data).includes(g.playerId))
              // .filter(player => [10, 9].includes(player.playerId))
              .map((player, id) => (
                <div key={id}>{player.displayName}</div>
              ))}
          </div>
        </div>
      ),
    },
    {
      title: t('pool'),
      dataIndex: 'result',
      key: 'result',
      render: data =>
        data?.map((item, id) => (
          <div className={classes.tableCell} key={id}>
            <MultiUnitStatistic
              key={id}
              valueStyle={{color: theme.textColor, fontSize: '12px', lineHeight: '30px'}}
              value={item?.potAmount}
            />
          </div>
        )),
    },
    {
      title: t('action'),
      render: (_value, record) => {
        return (
          <Button
            style={{
              height: 'fit-content',
              color: 'rgb(59, 153, 252)',
              padding: 0,
              textDecoration: 'underline',
            }}
            onClick={() => {
              getHistoryDetail(record.gameId, playerToken);
              setIsRecentGamesTableShown(false);
            }}
            type="text"
          >
            {t('detail')}
          </Button>
        );
      },
    },
  ];

  const renderWinnerInfo = (): JSX.Element => {
    if (historyDetailData?.length === 0 || !historyDetailData) return null;
    if (!isRecentGamesTableShown) {
      const gameResult = historyDetailData?.game?.result;

      let isEmpty: boolean = true;
      if (Array.isArray(gameResult) && gameResult?.length > 0) {
        const gameResultGroupByPlayer = historyDetailData?.game?.result[0]?.selectedCardsByPlayerId;
        if (gameResultGroupByPlayer) {
          isEmpty = Object.keys(gameResultGroupByPlayer ?? {}).length === 0;
        }
      }

      let playerList: Array<GamePlayersInterface>;

      if (historyDetailData.players) {
        playerList = historyDetailData.players;
      }
      const playersHandList: Array<PlayersHandListInterface> = [];
      let playersHand: PlayersHandListInterface;

      Object.keys(historyDetailData?.game || {}).map(key => {
        const gameStep = historyDetailData?.game[key];
        if (Array.isArray(gameStep?.playerActions)) {
          gameStep?.playerActions.map(item => {
            playersHand = {
              playerId: item.playerId,
              playerHand: item.playerHand,
            };

            const index = playersHandList.findIndex(object => object.playerId === item.playerId);

            if (index === -1) {
              playersHandList.push(playersHand);
            }
          });
        }
      });

      if (gameResult) {
        return (
          <section className={classes.winnerInfoArea}>
            <div className={classes.title}>{t('win_player')}</div>
            <div className={classes.content}>
              {gameResult?.map((item, id) => (
                <>
                  {item?.potWinnerIds?.map(potWinnerId => (
                    <WinnerCard
                      key={`${id}-${potWinnerId}`}
                      potId={item.potSequence}
                      containerWidthPx={110}
                      prize={item.winAmountForPlayer && changeUnit(item?.winAmountForPlayer)}
                      combination={
                        !isEmpty
                          ? item?.selectedCardsByPlayerId[potWinnerId]
                          : item?.selectedCards || []
                      }
                      playerName={getPlayer(potWinnerId, playerList)?.displayName}
                      iconUrl={getPlayer(potWinnerId, playerList)?.icon}
                      handCard={getPlayerHandCard(potWinnerId, playersHandList)}
                      showWinAmount
                    />
                  ))}
                </>
              ))}
            </div>
          </section>
        );
      }
    }
    return null;
  };

  const getPlayerHandCard = (
    playerId: number,
    playersHandListLocal: Array<PlayersHandListInterface>
  ) => {
    if (playersHandListLocal.length) {
      const result = playersHandListLocal.find(object => {
        return object.playerId === playerId;
      });
      if (result === undefined) return null;
      return result?.playerHand;
    }
    return null;
  };

  const getPlayer = (playerId: number, playerArray: Array<GamePlayersInterface>) => {
    if (playerArray) {
      const result = playerArray.find(obj => {
        return obj.playerId === playerId;
      });
      return result;
    }
    return null;
  };

  const renderPotAmount = (gameStepPerStreetLocal: any) => {
    if (gameStepPerStreetLocal) {
      const potArray = gameStepPerStreetLocal?.playerActions?.map(item => {
        return item.pot;
      });

      const maxPot = Math.max(...potArray);
      if (maxPot === -Infinity) return null;
      return maxPot;
    }
    return null;
  };

  const changeUnit = (originalValue: number): number => {
    const {unit, bigBlind} = game;
    const bigOriginalValue = new BigNumber(originalValue);
    const bigBigBlind = new BigNumber(bigBlind);
    const result = bigOriginalValue.dividedBy(bigBigBlind).toNumber();

    if (unit === UNIT_TYPE.BIG_BLIND) return result;
    return originalValue;
  };

  const renderGamesDetailColumn = ({
    id,
    key,
    isLoading,
  }: {
    id: number;
    key?: string;
    isLoading: Boolean;
  }): JSX.Element => (
    <div className={classes.tableColumn} key={`${id}-${key}`}>
      <div className={classes.tableTitleRow}>
        <div className={classes.titleColumn}>
          <div className={classes.label}>{turnName[id]}</div>
        </div>
      </div>
      <div className={classes.tableContentArea}>
        <div className={classes.tableContentColumn}>
          {isLoading && <div className={classes.loadingContent}>{t('loading')}</div>}
        </div>
      </div>
    </div>
  );

  const renderGamesDetail = () => {
    const noRecord = !historyDetailData;
    const turnOrder = [1, 2, 3, 4, 5];

    if (getLoading) {
      return <>{turnOrder.map(id => renderGamesDetailColumn({id, isLoading: true}))}</>;
    }

    if (noRecord) return <div className={classes.noRecord}>{t('game_review:no_game_record')} </div>;

    return Object.keys(historyDetailData.game).map((key, id) => {
      const gameStep = historyDetailData.game;
      const gameStepPerStreet = gameStep[key];
      let playerList: Array<GamePlayersInterface>;

      if (historyDetailData.players) {
        playerList = historyDetailData.players;
      }

      if (gameStepPerStreet?.playerActions || gameStepPerStreet?.communityCards)
        return (
          <div className={classes.tableColumn} key={id}>
            <div className={classes.tableTitleRow}>
              <div className={classes.titleColumn} key={id}>
                <div className={classes.label}>{turnName[id]}</div>
                {id === 1 ? null : (
                  <MultiUnitStatistic
                    valueStyle={{color: theme.secondaryTextColor, fontSize: '11px', marginTop: -5}}
                    value={renderPotAmount(gameStepPerStreet)}
                    formatter={compactFormatter}
                  />
                )}
              </div>
            </div>
            <div className={classes.tableContentArea}>
              <div className={classes.tableContentColumn}>
                {gameStepPerStreet?.playerActions?.map((item, stepId) => (
                  <div className={classes.playerInfoContainer} key={stepId}>
                    <PlayerInfo
                      icon={getPlayer(item.playerId, playerList)?.icon}
                      key={`${item.playerId}${item.action}${item.amount}${item.timestamp}${stepId}`}
                      containerWidthPx={50}
                      playerName={getPlayer(item.playerId, playerList)?.displayName}
                      playerAction={item.action}
                      isAllIn={item.isAllIn}
                      playerRole={item.role}
                      prefix={game.unit === UNIT_TYPE.USD && '$'}
                      suffix={game.unit === UNIT_TYPE.BIG_BLIND && 'BB'}
                      actionsAmount={item.amount && changeUnit(item.amount)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      else
        return (
          <>{id !== 0 && id !== 6 ? renderGamesDetailColumn({id, key, isLoading: false}) : null}</>
        );
    });
  };

  const renderLoadingArea = (): JSX.Element => (
    <div key={0} className={classes.loadingArea}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
        style={{height: '20px'}}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 5.25l-7.5 7.5-7.5-7.5m15 6l-7.5 7.5-7.5-7.5"
        />
      </svg>
    </div>
  );

  const displayLoadingFinishedMessage = (): JSX.Element => (
    <div className={classes.loadingArea}>{t('all_record_loaded')}</div>
  );

  const handleInfiniteOnLoad = async () => {
    pageNumberRef.current += 1;

    const currentList = recentGameList;
    const newRooms = await gameApi.getRecentGames({
      roomCode: roomNumber,
      playerToken,
      limit: API_PARAMS.ROOM_AMOUNT,
      page: pageNumberRef.current,
    });
    const accumulatedList = currentList.concat(newRooms);

    if (currentList.length === accumulatedList.length) {
      setHasMoreData(false);
    }
    setRecentGameList(accumulatedList);
  };

  const renderTables = (): JSX.Element => {
    if (isRecentGamesTableShown)
      return (
        <div className={classes.infiniteScrollContainer}>
          <InfiniteScroll
            initialLoad={false}
            loadMore={handleInfiniteOnLoad}
            hasMore={hasMoreData}
            threshold={1}
            useWindow={false}
            loader={renderLoadingArea()}
          >
            <ConfigProvider
              renderEmpty={componentName => {
                if (componentName === 'Table') {
                  return <Skeleton style={{background: theme.backgroundColor}} active />;
                }
              }}
              theme={{
                components: {
                  Table: {
                    headerBg: theme.backgroundColor,
                    headerColor: theme.secondaryTextColor,
                    cellFontSize: 12,
                    borderColor: 'transparent',
                    rowHoverBg: 'transparent',
                    headerSplitColor: 'transparent',
                    headerBorderRadius: 0,
                  },
                },
              }}
            >
              <Table
                onHeaderRow={() => {
                  return {style: {fontSize: 12, fontWeight: '400'}};
                }}
                pagination={false}
                bordered={false}
                size="small"
                locale={tableLocale}
                loading={tableLoading}
                columns={recentGamesColumn}
                dataSource={recentGameList?.map((data, id) => ({key: id, ...data}))}
                className={classes.table}
              />
              {!hasMoreData && recentGameList.length >= 1 && displayLoadingFinishedMessage()}
            </ConfigProvider>
          </InfiniteScroll>
        </div>
      );
    return (
      <section className={classes.detailTableArea}>
        <div className={classes.tableContentArea}>
          <section className={classes.detailTableArea}>{renderGamesDetail()}</section>
        </div>
      </section>
    );
  };

  return (
    <section className={classes.container}>
      {renderTables()}
      {renderWinnerInfo()}
      <div className={classes.buttonRow}>
        {!isRecentGamesTableShown ? (
          <Button
            size="small"
            ghost
            className={classes.previousButton}
            onClick={() => setIsRecentGamesTableShown(true)}
          >
            {t('previous_page')}
          </Button>
        ) : null}
        <Button
          size="small"
          ghost
          className={classes.previousButton}
          onClick={() => {
            closeDrawer();
          }}
        >
          {t('close')}
        </Button>
      </div>
    </section>
  );
};

export default HistoryPanel;
